'use client';

import { useRouter } from 'next/navigation';
import { usePathname } from 'next/navigation';
import { MouseEvent, ReactNode } from 'react';
import {
    normalizePath,
    scrollToAnchor,
    setPendingAnchor,
    splitAnchor,
} from '@/lib/anchorScroll';

type AnchorButtonLinkProps = {
    href: string;
    className?: string;
    'data-umami-event'?: string;
    children?: ReactNode;
};

/**
 * Link auf einen Anker – entweder auf dieser Seite (`#id`) oder auf einer
 * anderen (`/seite#id`).
 *
 * Der Hash landet nie in der URL: auf derselben Seite wird direkt gescrollt
 * (dadurch beliebig oft wiederholbar – hin, hochscrollen, wieder hin), bei
 * einem Seitenwechsel wird der Anker gemerkt und nach dem Rendern der Zielseite
 * von <AnchorScroll> angesprungen.
 *
 * Das `href` bleibt vollständig (inkl. Hash) am <a>, damit Mittelklick,
 * "In neuem Tab öffnen" und Crawler weiterhin das echte Ziel sehen.
 */
export default function AnchorButtonLink({ href, children, ...props }: AnchorButtonLinkProps) {
    const router = useRouter();
    const pathname = usePathname();

    const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
        // Nur normale Linksklicks abfangen (kein Ctrl/Cmd/Shift/Middle-Click).
        if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
            return;
        }

        const { path, anchor } = splitAnchor(href);
        if (!anchor) return;

        const samePage = !path || normalizePath(path) === normalizePath(pathname);

        event.preventDefault();

        if (samePage) {
            scrollToAnchor(anchor);
            return;
        }

        setPendingAnchor(anchor);
        router.push(normalizePath(path));
    };

    return (
        <a href={href} onClick={handleClick} {...props}>
            {children}
        </a>
    );
}
